// src/components/AuthTimeout.jsx
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../Config";

export default function AuthTimeout() {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [showTimeout, setShowTimeout] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30 * 60); // 30 minutes initialement
  const [displayTime, setDisplayTime] = useState("30:00");
  
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const intervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Fonction pour formater le temps
  const formatTime = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? '0' : ''}${secs}`;
  }, []);

  // Mettre à jour l'affichage du temps
  useEffect(() => {
    setDisplayTime(formatTime(timeLeft));
    
    // Mettre à jour dans localStorage pour partager avec MainLayout
    localStorage.setItem('sessionTimeLeft', timeLeft.toString());
  }, [timeLeft, formatTime]);

  // Mettre à jour la dernière activité
  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    localStorage.setItem('lastActivity', lastActivityRef.current.toString());
    
    // Réinitialiser le timer à 30 minutes
    setTimeLeft(30 * 60);
    
    // Réinitialiser les timers d'avertissement
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    
    // Avertir dans 25 minutes (5 minutes avant expiration)
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
    }, 25 * 60 * 1000);
  }, []);

  const startTimers = useCallback(() => {
    // Nettoyer les anciens timers
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    // Timer principal (30 minutes)
    logoutTimerRef.current = setTimeout(() => {
      setShowWarning(false);
      setShowTimeout(true);
      
      setTimeout(() => {
        localStorage.clear();
        navigate("/login", { replace: true });
      }, 3000);
    }, 30 * 60 * 1000);

    // Avertissement (25 minutes)
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
    }, 25 * 60 * 1000);

    // Timer pour le compte à rebours
    intervalRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    // Récupérer la dernière activité
    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity) {
      lastActivityRef.current = parseInt(lastActivity);
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      const timePassedSeconds = Math.floor(timeSinceLastActivity / 1000);
      const initialTimeLeft = Math.max(0, (30 * 60) - timePassedSeconds);
      
      setTimeLeft(initialTimeLeft);
      
      // Si moins de 30 minutes se sont écoulées, démarrer les timers
      if (timeSinceLastActivity < 30 * 60 * 1000) {
        startTimers();
      } else {
        // Sinon, déconnecter immédiatement
        setShowTimeout(true);
        setTimeout(() => {
          localStorage.clear();
          navigate("/login", { replace: true });
        }, 3000);
      }
    } else {
      // Première connexion
      updateLastActivity();
      startTimers();
    }

    // Écouter les interactions utilisateur
    const events = ['mousedown', 'keydown', 'click', 'scroll', 'touchstart'];
    const handleUserActivity = () => {
      updateLastActivity();
      setShowWarning(false);
    };

    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [navigate, startTimers, updateLastActivity]);

  const handleCloseWarning = () => {
    setShowWarning(false);
    updateLastActivity();
  };

  const handleStayLoggedIn = () => {
    setShowWarning(false);
    updateLastActivity();
  };

  return (
    <>
      {/* Avertissement 5 minutes avant */}
      {showWarning && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full mx-auto mb-4">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Session sur le point d'expirer
            </h3>
            
            <div className="text-center mb-4">
              <div className="text-2xl font-bold text-gray-900 mb-2">
                {displayTime}
              </div>
              <p className="text-sm text-gray-600">
                Votre session expirera dans {displayTime}.
                Interagissez avec l'application pour la maintenir active.
              </p>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
              <div 
                className="bg-red-500 h-2 rounded-full transition-all duration-1000"
                style={{ width: `${(timeLeft / (30 * 60)) * 100}%` }}
              ></div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleCloseWarning}
                className="flex-1 py-2 border border-gray-300 text-gray-700 rounded hover:"
              >
                Fermer
              </button>
              <button
                onClick={handleStayLoggedIn}
                className="flex-1 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Rester connecté
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alerte de déconnexion */}
      {showTimeout && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-70">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Session expirée
            </h3>
            
            <p className="text-gray-600 text-center mb-6">
              Votre session a expiré (30 minutes d'inactivité).
              Vous allez être redirigé vers la page de connexion.
            </p>

            <div className="flex justify-center">
              <button
                onClick={() => {
                  localStorage.clear();
                  navigate("/login", { replace: true });
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Se reconnecter
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}